import type { Theme } from '../../contract.js';
import { cyberLight, cyberDark } from './variants.js';

/**
 * Cyber family — cyberpunk theme with harmonic-wave shader.
 *
 * Ships paired with the kWh-milestone unlock at 1150 kWh (the
 * 4th and final tier in the post-rewrite unlock model — the
 * "elite" reward). Family registry entry exposes the theme to
 * the picker; the `skin_cyber` Unlock doc gates visibility via
 * the milestone-evaluator. Commit 1 of the unlock-model rewrite
 * reactivated the evaluator; Commit 2 staged the placeholder;
 * this commit flips `is_active` true via the seeds/unlocks.ts
 * edit + the --activate-cyber migration step.
 *
 * Charging animation: the cyber shader (shaderKind: 'cyber')
 * with per-mode variant (cyber-dark / cyber-light). The shader
 * source is adapted from the shadertoy "Harmonic Pixels" demo
 * — 50 oscillating orbs across three channels (150 inner
 * iterations per pixel) layered through a CRT-style scanline-
 * modulated background — with uniform-injected palettes per
 * variant and uKwhScale-driven amplitude growth.
 *
 * Map style: flat Mapbox light/dark (NOT satellite). Cyber is a
 * UI aesthetic, not a map context; satellite imagery would
 * compete with the shader's wave composition for visual
 * attention. Flat basemaps stay quiet and let the charging
 * animation own the visual identity.
 */
export const cyberTheme: Theme = {
  id: 'cyber',
  name: 'Cyber',
  description: 'Cyberpunk aesthetic with magenta and electric purple harmonic waves.',
  // Picker swatches — hot magenta + electric purple + dark navy
  // capture the dark-variant cyber identity. The picker's
  // per-row SwatchRow renders from variant token fields
  // directly; this top-level array is read by
  // packages/ui DriverThemePicker for catalogue display.
  swatches: ['#F233D9', '#8C4DF2', '#0A0E26'],
  variants: { light: cyberLight, dark: cyberDark },
  // Cyber is milestone-gated, NOT default-unlocked. The
  // `unlockedByDefault: false` here matches the
  // `skin_cyber.is_active` state in the seed; gating logic
  // lives in the Unlock catalog + UserUnlock grants, not in
  // the registry. Setting this to true would NOT bypass the
  // unlock gate (the registry entry is purely structural —
  // unlock state is server-evaluated).
  unlockedByDefault: false,
  costPoints: 0,
  // Satellite-streets for both modes, matching the pattern
  // default / neon / obsidian / electric-light (and other shader
  // families) already follow. Satellite imagery has no real
  // "dark variant" — the same photo-real ground texture renders
  // regardless of mode. The mode toggle still flips chrome +
  // shader; only the map base stays constant.
  mapStyle: {
    light: 'mapbox://styles/mapbox/satellite-streets-v12',
    dark: 'mapbox://styles/mapbox/satellite-streets-v12',
  },
  chargingAnimation: 'shader',
  shaderKind: 'cyber',
  // Variant selector — Theme.shaderVariant is mode-dependent
  // for cyber. Picked dynamically by the dispatcher's per-mode
  // resolver (added during Aurora's build) based on the active
  // ThemeProvider mode (light → cyber-light, dark → cyber-dark).
  // The registry entry below carries the dark variant as the
  // default; runtime mode resolution overrides.
  shaderVariant: 'cyber-dark',
};
